import {
  ErrorResponseSchema,
  Value,
  type ErrorResponseContract,
  type Static,
  type TSchema,
} from "@pakker/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly response: ErrorResponseContract,
  ) {
    super(response.error.message);
    this.name = "ApiError";
  }
}

export class ApiNetworkError extends Error {
  constructor(options: { cause?: unknown } = {}) {
    super(
      "The server response could not be confirmed.",
      options.cause === undefined ? undefined : { cause: options.cause },
    );
    this.name = "ApiNetworkError";
  }
}

export interface ApiRequestOptions<T extends TSchema> {
  readonly schema: T;
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  readonly orgId?: string;
  readonly signal?: AbortSignal;
}

export async function apiRequest<T extends TSchema>(
  path: string,
  options: ApiRequestOptions<T>,
): Promise<Static<T>> {
  const method = options.method ?? "GET";
  const headers = new Headers({ Accept: "application/json" });
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (method !== "GET") {
    headers.set("X-CSRF-Protection", "1");
  }
  if (options.orgId !== undefined) {
    headers.set("X-Pakker-Org-Id", options.orgId);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      cache: method === "GET" ? "default" : "no-store",
      method,
      headers,
      credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiNetworkError({ cause });
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch (cause) {
    throw new ApiNetworkError({ cause });
  }

  if (!response.ok) {
    if (Value.Check(ErrorResponseSchema, payload)) {
      throw new ApiError(response.status, payload);
    }
    throw new ApiNetworkError();
  }
  if (!Value.Check(options.schema, payload)) {
    throw new ApiNetworkError();
  }
  return payload;
}
